/** Seat-name tiers for the default ICS org-chart layout. */

export const INCIDENT_COMMANDER_SEAT = 'Incident Commander';
export const AREA_OR_DISTRICT_COMMAND_SEAT = 'Area or District Command';

/** TEMA State EOC — treated as Incident Commander for layout / IC aliasing. */
export const TEMA_DIRECTOR_SEAT = 'TEMA Director';
export const TEMA_DACO_SEAT = 'Direction and Coordination Officer (DACO)';

export const COMMAND_STAFF_SEATS = [
  'Public Information Officer',
  'Liaison Officer',
  'Safety Officer',
  'Legal Officer',
] as const;

/** EMSI-style left flank of the IC spine (top → bottom). */
export const COMMAND_STAFF_LEFT_SEATS = [
  'Public Information Officer',
  'Legal Officer',
] as const;

/** EMSI-style right flank of the IC spine (top → bottom). */
export const COMMAND_STAFF_RIGHT_SEATS = [
  'Liaison Officer',
  'Safety Officer',
] as const;

/** TEMA command flanks beside TEMA Director. */
export const TEMA_COMMAND_STAFF_LEFT_SEATS = [
  'State Emergency Info Director (SEID)',
] as const;

export const TEMA_COMMAND_STAFF_RIGHT_SEATS = [
  'Private Sector Coordinator',
] as const;

export const SECTION_CHIEF_SEATS = [
  'Operations Section Chief',
  'Planning Section Chief',
  'Logistics Section Chief',
  'Finance Section Chief',
  'Intel/Investigations Section Chief',
] as const;

/** TEMA section boxes under DACO (left → right on the crossbar). */
export const TEMA_SECTION_SEATS = [
  'MCC Section',
  'Operations Section',
  'Planning Section',
  'Logistics Section',
  'Finance and Admin Section',
] as const;

export type RosterSeatTier =
  | 'areaCommand'
  | 'incidentCommander'
  | 'commandStaff'
  | 'sectionChief'
  | 'other';

const COMMAND_STAFF_SET = new Set<string>([
  ...COMMAND_STAFF_SEATS,
  ...TEMA_COMMAND_STAFF_LEFT_SEATS,
  ...TEMA_COMMAND_STAFF_RIGHT_SEATS,
]);
const SECTION_CHIEF_SET = new Set<string>([
  ...SECTION_CHIEF_SEATS,
  ...TEMA_SECTION_SEATS,
]);
const INCIDENT_COMMANDER_SET = new Set<string>([
  INCIDENT_COMMANDER_SEAT,
  TEMA_DIRECTOR_SEAT,
]);

export function isTemaDirectorSeat(name: string): boolean {
  return name === TEMA_DIRECTOR_SEAT;
}

export function isTemaSectionSeat(name: string): boolean {
  return (TEMA_SECTION_SEATS as readonly string[]).includes(name);
}

export function isTemaRccSeat(name: string): boolean {
  return name.endsWith(' RCC');
}

export function isTemaLouSeat(name: string): boolean {
  return name === 'Logistics Operating Unit (LOU)';
}

export function classifySeat(name: string): RosterSeatTier {
  if (name === AREA_OR_DISTRICT_COMMAND_SEAT) return 'areaCommand';
  if (INCIDENT_COMMANDER_SET.has(name)) return 'incidentCommander';
  if (COMMAND_STAFF_SET.has(name)) return 'commandStaff';
  if (SECTION_CHIEF_SET.has(name)) return 'sectionChief';
  return 'other';
}

export function isCommandStaffSeat(name: string): boolean {
  return COMMAND_STAFF_SET.has(name);
}

export function isSectionChiefSeat(name: string): boolean {
  return SECTION_CHIEF_SET.has(name);
}

export function isIncidentCommanderLayoutSeat(name: string): boolean {
  return INCIDENT_COMMANDER_SET.has(name);
}

/** Stable left-to-right order for known ICS seats; unknowns sort last by name. */
export function compareIcsSeatNames(a: string, b: string): number {
  const order = [
    AREA_OR_DISTRICT_COMMAND_SEAT,
    INCIDENT_COMMANDER_SEAT,
    TEMA_DIRECTOR_SEAT,
    ...COMMAND_STAFF_SEATS,
    ...TEMA_COMMAND_STAFF_LEFT_SEATS,
    ...TEMA_COMMAND_STAFF_RIGHT_SEATS,
    TEMA_DACO_SEAT,
    ...SECTION_CHIEF_SEATS,
    ...TEMA_SECTION_SEATS,
  ];
  const ai = order.indexOf(a);
  const bi = order.indexOf(b);
  if (ai !== -1 && bi !== -1) return ai - bi;
  if (ai !== -1) return -1;
  if (bi !== -1) return 1;
  return a.localeCompare(b);
}

export function compareTemaSectionNames(a: string, b: string): number {
  const order = TEMA_SECTION_SEATS as readonly string[];
  const ai = order.indexOf(a);
  const bi = order.indexOf(b);
  if (ai !== -1 && bi !== -1) return ai - bi;
  if (ai !== -1) return -1;
  if (bi !== -1) return 1;
  return a.localeCompare(b);
}
